import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme, alpha } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import UpdateIcon from "@mui/icons-material/Update";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import TimelineIcon from "@mui/icons-material/Timeline";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

function PersonalInfoTab({ userDetails, token, onVerifyEmail }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Grid container spacing={{ xs: 3, md: 4 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PersonIcon />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {t("userProfile.basicInfo", "Basic Information")}
            </Typography>
          </Box>

          <List sx={{ py: 0 }}>
            <ListItem sx={{ px: 0, py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PersonIcon color="action" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={t("userProfile.fullNameLabel")}
                secondary={
                  `${userDetails?.first_name || ""} ${userDetails?.last_name || ""}`.trim() ||
                  t("userProfile.unknownName")
                }
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                  fontWeight: 500,
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: "text.primary",
                  fontWeight: 600,
                  mt: 0.5,
                }}
              />
            </ListItem>

            <Divider />

            <ListItem sx={{ px: 0, py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <BadgeIcon color="action" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={t("userProfile.usernameLabel")}
                secondary={
                  userDetails?.username || t("userProfile.unknownUsername")
                }
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                  fontWeight: 500,
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: "text.primary",
                  fontWeight: 600,
                  mt: 0.5,
                }}
              />
            </ListItem>

            <Divider />

            <ListItem sx={{ px: 0, py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <EmailIcon color="action" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.875rem",
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      {t("userProfile.emailLabel", "Email")}
                    </Box>
                  </Box>
                }
                secondary={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "1rem",
                        color: "text.primary",
                        fontWeight: 600,
                      }}
                    >
                      {userDetails?.email || t("userProfile.unknownEmail")}
                    </Box>
                    {!userDetails?.is_email_verified && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={onVerifyEmail}
                        disabled={!token}
                        sx={{
                          fontSize: "0.75rem",
                          textTransform: "none",
                          fontWeight: 600,
                          minWidth: "auto",
                          p: 0.5,
                        }}
                      >
                        {t("userProfile.verify", "Verify")}
                      </Button>
                    )}
                  </Box>
                }
                primaryTypographyProps={{
                  component: "div",
                }}
                secondaryTypographyProps={{
                  component: "div",
                }}
              />
            </ListItem>
          </List>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                color: "secondary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TimelineIcon />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {t("userProfile.accountStatus", "Account Status")}
            </Typography>
          </Box>

          <List sx={{ py: 0 }}>
            <ListItem sx={{ px: 0, py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CalendarTodayIcon color="action" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={t("userProfile.accountCreated", "Account Created")}
                secondary={convertUTCToLocal(userDetails?.created_at)}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                  fontWeight: 500,
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: "text.primary",
                  fontWeight: 600,
                  mt: 0.5,
                }}
              />
            </ListItem>

            <Divider />

            <ListItem sx={{ px: 0, py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <UpdateIcon color="action" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={t("userProfile.lastUpdated", "Last Updated")}
                secondary={convertUTCToLocal(userDetails?.updated_at)}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                  fontWeight: 500,
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: "text.primary",
                  fontWeight: 600,
                  mt: 0.5,
                }}
              />
            </ListItem>

            <Divider />

            <ListItem sx={{ px: 0, py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <EmailIcon color="action" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "userProfile.emailVerification",
                  "Email Verification"
                )}
                secondary={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    {userDetails?.is_email_verified ? (
                      <Chip
                        icon={<VerifiedUserIcon />}
                        label={t("userProfile.verified", "Verified")}
                        size="small"
                        color="success"
                        sx={{ height: 24, fontSize: "0.75rem" }}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Chip
                          label={t("userProfile.unverified", "Unverified")}
                          size="small"
                          color="warning"
                          sx={{ height: 24, fontSize: "0.75rem" }}
                        />
                        <Button
                          variant="text"
                          size="small"
                          onClick={onVerifyEmail}
                          disabled={!token}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            minWidth: "auto",
                          }}
                        >
                          {t("userProfile.verify", "Verify")}
                        </Button>
                      </Box>
                    )}
                  </Box>
                }
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                  fontWeight: 500,
                }}
                secondaryTypographyProps={{
                  component: "div",
                }}
              />
            </ListItem>
          </List>
        </Paper>
      </Grid>
    </Grid>
  );
}

PersonalInfoTab.propTypes = {
  userDetails: PropTypes.object,
  token: PropTypes.string,
  onVerifyEmail: PropTypes.func.isRequired,
};

export default PersonalInfoTab;
